import { CalendarClock } from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { JobProducts } from './JobProducts';
import { PricesSection } from './prices/PricesSection';
import type { VetterJob } from './prices/price-form';
import { ProductionSection } from './ProductionSection';
import { ShippingSection } from './ShippingSection';

/**
 * The header the LINK page shows: the supplier has no other context, so it
 * says who the request is from and for.
 */
export function LinkJobHeader({ job }: { job: VetterJob }) {
	return (
		<header className="mb-10">
			<div className="text-sm text-text-4">
				Price request from 05 Creation
				{job.vetterEntity?.name ? ` for ${job.vetterEntity.name}` : ''}
			</div>
			<h1 className="mt-1 text-3xl font-medium -tracking-[0.02em] text-text-1">
				{job.quoteName}
			</h1>
			{job.quoteDeadline && (
				<div className="mt-2 flex items-center gap-1.5 text-sm text-text-3">
					<CalendarClock size={13} />
					Goods needed by {formatDate(job.quoteDeadline)}
				</div>
			)}
		</header>
	);
}

/**
 * When the price form starts over from the server: after each submission, and
 * when the studio's delivery options change — their rows are what the form
 * is built from.
 */
const formKey = (job: VetterJob) =>
	[
		job.submittedAt ?? 'new',
		...job.items.flatMap((i) =>
			i.tiers.flatMap((t) =>
				t.deliveryOptions.flatMap((o) => [o.id, ...o.parts.map((p) => p.id)])
			)
		),
	].join('|');

/**
 * One job's prices / production / shipping sections, under a header.
 *
 * Shared by both ways in — the link's `/job?token=` page and the portal's
 * `/jobs/:id` — which differ in how they load the job, what chrome surrounds
 * it, and so what the header needs to say.
 */
export function JobDetail({
	job,
	header,
	onChanged,
}: {
	job: VetterJob;
	header: React.ReactNode;
	/** Refetches the job after any action. */
	onChanged: () => Promise<unknown>;
}) {
	return (
		<>
			{header}
			<div className="flex flex-col gap-14">
				<JobProducts job={job} onCountsStale={onChanged} />
				{/* Keyed on the submission so the form resets to what the server holds
				    after each send, rather than keeping the pre-send draft. */}
				<PricesSection key={formKey(job)} job={job} onSubmitted={onChanged} />
				{job.orderItems.length > 0 && (
					<ProductionSection orderItems={job.orderItems} onChanged={onChanged} />
				)}
				{job.shipments.length > 0 && (
					<ShippingSection shipments={job.shipments} onChanged={onChanged} />
				)}
			</div>
		</>
	);
}
